import { useEffect, useState } from 'react';
import { COLORS, INPUT_STYLE } from '../../constants/theme';

interface NumericInputProps {
  value: number;
  onChange: (v: number) => void;
  min?: number;
  max?: number;
  step?: number;
  className?: string;
  ariaLabel?: string;
  /** SF-141: bump this to force the input to drop any in-progress typed `draft` and re-derive its
   *  displayed text from `value` — fixes external bulk mutations (Fill Strategy/Clear/submit
   *  success) leaving a mid-edit input showing stale local text. Omitted = never resets. */
  resetKey?: number;
  /** SF-141: `'touch'` renders a 16px-font/44px-min-height variant (avoids iOS Safari's sub-16px
   *  auto-zoom). Default preserves the exact existing `py-0.5 text-sm` styling. */
  size?: 'default' | 'touch';
}

const SIZE_CLASSES: Record<NonNullable<NumericInputProps['size']>, string> = {
  default: 'py-0.5 text-sm',
  touch: 'py-2.5 text-base',
};

/**
 * Controlled numeric input that allows clearing the field while typing.
 * On blur, empty input reverts to 0.
 */
export function NumericInput({ value, onChange, min = 0, max, step = 1, className = 'w-full', ariaLabel, resetKey, size = 'default' }: NumericInputProps) {
  const [draft, setDraft] = useState<string | null>(null);

  useEffect(() => {
    if (resetKey !== undefined) setDraft(null);
  }, [resetKey]);

  return (
    <input
      type="number"
      aria-label={ariaLabel}
      min={min}
      max={max}
      step={step}
      value={draft !== null ? draft : value}
      onChange={(e) => {
        const raw = e.target.value;
        setDraft(raw);
        if (raw !== '') onChange(Math.max(min, parseFloat(raw) || 0));
      }}
      onFocus={(e) => { setDraft(null); e.target.select(); }}
      onBlur={() => {
        if (draft === '') onChange(0);
        setDraft(null);
      }}
      className={`rounded text-right font-mono ${SIZE_CLASSES[size]} ${className}`}
      style={{ ...INPUT_STYLE, border: `1px solid ${COLORS.inputBorder}`, minHeight: size === 'touch' ? 44 : undefined }}
    />
  );
}
